import { Avatar, Box, Tooltip, Typography } from '@mui/material'
import { GridRenderCellParams, getGridStringOperators } from '@mui/x-data-grid-premium'
import AccountingCommentCell from 'components/Accounting/grid/cells/AccountingCommentCell'
import { customNotContainsOperator } from 'components/DataGrid/operators/customNotContainsOperator'
import {
  customDateSortOperator,
  customNumericSortOperator,
  customSortOperator,
} from 'components/DataGrid/operators/customSortOperator'
import { getDensityStyles } from 'components/DataGrid/operators/getDensityStyles'
import { strictEqualsOperator } from 'components/DataGrid/operators/strictEqualsOperator'
import RenderDateCell from 'components/Deals/grid/cells/RenderDateCell'
import RenderDealNameCell from 'components/Deals/grid/cells/RenderDealNameCell'
import CommentCellHeader from 'components/MTB/grid/cells/CommentCellHeader'
import dayjs from 'dayjs'
import { Link } from 'react-router-dom'
import { formatDateTime } from 'utils/formatDayjsDateTime'
import { GridStageChip, GridStatusChip, NcoStatusContainer } from '../../../styles/elements/styleElements'
import { formatCurrency } from '../../DealsKanban/aggregateStageTotals'
import EditSelectCell from '../../NCO/EditSelectCell'
import RenderVertical from '../../Organizations/RenderVertical'
import PandaDocViewer from '../../PaymentSchedule/PandDocViewer'
import { renderColorByString } from '../renderColorByString'
import RenderContractedByColumn from '../RenderContractedByColumn'

export const capitalizeFirstLetter = string => {
  return string.charAt(0).toUpperCase() + string.slice(1)
}
export const formatDayjsDate = dateStr => {
  // console.log('[renderDealsColumns][formatDayjsDate] dateStr: ', dateStr)
  if (dateStr) {
    return dayjs(dateStr).format('MM/DD/YYYY hh:mm A')
  } else {
    return null
  }
}
export const formatDayJsTime = dateStr => {
  // console.log('[renderDealsColumns][formatDayjsDate] dateStr: ', dateStr)
  if (dateStr) {
    return dayjs(dateStr).format('hh:mm A')
  } else {
    return null
  }
}
const formatDateString = date => {
  if (!date) return ''
  return dayjs.isDayjs(date) ? date.format('MM/DD/YYYY') : dayjs(date).format('MM/DD/YYYY')
}
export const stringAvatar = (name = '', fontsize) => {
  // Check if name is undefined or an empty string
  const nameParts = name ? name.split(' ') : ['']

  if (nameParts.length < 2) {
    return {
      children: (
        <Typography
          sx={{
            fontSize: fontsize ? fontsize : 14,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: '100%',
            height: '100%',
          }}
        >
          {nameParts[0][0] || ''} {/* Handle the case where nameParts[0][0] is undefined */}
        </Typography>
      ),
    }
  }

  return {
    children: (
      <Typography
        sx={{
          fontSize: fontsize ? fontsize : 14,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: '100%',
          height: '100%',
        }}
      >
        {`${nameParts[0][0]}${nameParts[1][0]}`}
      </Typography>
    ),
  }
}
export const renderAccountingColumns = (users, deals, density = 'standard', handleDealClick, theme) => {
  const densityStyles = getDensityStyles(density)
  return [
    {
      field: 'name',
      headerName: 'Deal Name',
      width: 175,
      headerAlign: 'left',
      valueGetter: params => {
        return params
      },
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      renderCell: params => {
        return (
          // <Box
          //   onClick={() => handleDealClick(params.row)}
          // sx={{
          //   cursor: 'pointer',
          //   height: '100%',
          //   width: '100%',
          //   overflow: 'hidden',
          //   textOverflow: 'ellipsis',
          //   whiteSpace: 'nowrap',
          // }}
          // >
          //   <Typography
          //     sx={{
          //       fontSize: densityStyles.fontSize,
          //       overflow: 'hidden',
          //       textOverflow: 'ellipsis',
          //       whiteSpace: 'nowrap',
          //     }}
          //   ></Typography>
          //   {params.value}
          // </Box>
          <Box
            sx={{
              cursor: 'pointer',
              height: '100%',
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            onClick={() => handleDealClick(params.row)}
          >
            <RenderDealNameCell params={params} navigate={null} densityStyles={densityStyles} />
          </Box>
        )
      },
    },
    {
      field: 'commentCount',
      aggregable: false,
      pinnable: false,
      width: 40,
      headerName: 'Comments',
      disableColumnMenu: true,
      renderHeader: () => <CommentCellHeader densityStyles={densityStyles} />,
      align: 'center',
      sortComparator: customNumericSortOperator,
      valueGetter: (_, row) => {
        const count = row?.count ?? 0
        if (
          row?.PK === 'ORG_c7982d02-753c-4deb-94fc-8726729402eb' &&
          row?.SK === 'DEAL_21add3e4-fd9d-4a44-a1b1-ba4d32e06c24'
        )
          return count
      },
      renderCell: (params: GridRenderCellParams) => {
        const { row } = params ?? {}
        return (
          <AccountingCommentCell
            params={params}
            densityStyles={densityStyles}
            route={`/accounting/${row?.PK}/${row?.SK}/comments`}
          />
        )
      },
    },
    {
      field: 'contractId',
      headerName: 'Contract',
      width: 75,
      headerAlign: 'center',
      valueGetter: params => {
        return params ?? null
      },
      renderCell: params => {
        const previewUrl = `https://app.pandadoc.com/a/#/documents`
        const contractId = params && params.value ? params.value : null
        const contractUrl = previewUrl && contractId ? `${previewUrl}/${contractId}` : null
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            {contractUrl ? (
              <PandaDocViewer
                contractId={contractId}
                customFileName={`${params?.row?.name}`}
                size={{ height: densityStyles.globeSize, width: densityStyles.globeSize }}
              />
            ) : (
              // <DisabledIconButton disabled>
              //   <PandaDocLinkIcon size={densityStyles.globeSize} />
              // </DisabledIconButton>
              <PandaDocViewer
                contractId={contractId}
                customFileName={`${params?.row?.name}`}
                size={{ height: densityStyles.globeSize, width: densityStyles.globeSize }}
                disabled={true}
              />
            )}
          </Box>
        )
      },
    },
    {
      field: 'dba',
      headerName: 'Legal Entity Name',
      width: 125,
      headerAlign: 'center',
      valueGetter: (_, row) => {
        return row?.organization?.dba ?? null
      },
      renderCell: params => {
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'left',
              width: '100%',
              height: '100%',
            }}
          >
            {params.value ? (
              <Typography sx={{ fontSize: 12 }}>{params.value}</Typography>
            ) : (
              <Typography sx={{ color: 'rgb(60, 57, 57)', fontSize: 14 }}>{'—'}</Typography>
            )}
          </Box>
        )
      },
    },
    {
      field: 'invoiceStatus',
      headerName: 'Invoice Status',
      width: 100,
      headerAlign: 'center',
      editable: true,
      valueGetter: params => {
        // Default to "Not Invoiced" if the value is null or undefined
        return params === 'Invoiced' ? 'Invoiced' : 'Not Invoiced'
      },
      filterOperators: [...getGridStringOperators(), strictEqualsOperator],
      renderCell: params => {
        const handleClick = () => {
          params.api.startCellEditMode({
            id: params.id,
            field: params.field,
          })
        }
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              // padding: '2px', // Adjust padding to ensure no overflow
              boxSizing: 'border-box', // Include padding in the size calculation
              cursor: 'pointer',
            }}
            onClick={handleClick}
          >
            <NcoStatusContainer
              size="small"
              type={params.value}
              sx={{ bgcolor: renderColorByString(params?.value ?? 'Not Invoiced'), textAlign: 'center' }}
            >
              {params.value}
            </NcoStatusContainer>
          </Box>
        )
      },
      renderEditCell: params => (
        <EditSelectCell
          params={params}
          options={[
            { value: 'Invoiced', label: 'Invoiced' },
            { value: 'Not Invoiced', label: 'Not Invoiced' },
          ]}
        />
      ),
    },
    {
      field: 'billingStatus',
      headerName: 'Billing Status',
      width: 100,
      headerAlign: 'center',
      editable: true,
      valueGetter: (_, row) => row?.billingStatus ?? 'Set Billing Status',
      filterOperators: [...getGridStringOperators(), strictEqualsOperator],
      renderCell: params => {
        const handleClick = () => {
          params.api.startCellEditMode({ id: params.id, field: params.field })
        }
        return (
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}
            onClick={handleClick}
          >
            <NcoStatusContainer
              size="small"
              type={params.value}
              sx={{ bgcolor: renderColorByString(params.value), textAlign: 'center' }}
            >
              {params.value}
            </NcoStatusContainer>
          </Box>
        )
      },
      renderEditCell: params => (
        <EditSelectCell
          params={params}
          options={[
            { value: 'Current (<60)', label: 'Current (<60)' },
            { value: 'Past Due (60+)', label: 'Past Due (60+)' },
            { value: 'Past Due (90+)', label: 'Past Due (90+)' },
            { value: 'Potential Bad Debt', label: 'Potential Bad Debt' },
          ]}
        />
      ),
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 100,
      headerAlign: 'center',
      valueGetter: params => {
        return params
      },
      renderCell: params => {
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'left',
              width: '100%',
            }}
          >
            <GridStatusChip size="small" type={params.value} label={params.value} />
          </Box>
        )
      },
    },
    {
      field: 'invoiceOption',
      headerName: 'Invoice Option',
      width: 100,
      headerAlign: 'center',
      valueGetter: (_, row) => {
        const option = row?.invoiceOption
        if (option === 'Clone & Replace Existing Invoice') return 'Revise Existing Invoice'
        return option
      },
      renderCell: params => {
        const isRevise = params?.value === 'Revise Existing Invoice'
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'left',
              width: '100%',
              height: '100%',
              color: isRevise ? 'text.warning' : 'text.primary',
              fontWeight: isRevise ? 'bolder' : '',
              textAlign: 'left',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            <Typography sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 12 }}>
              {params?.value}
            </Typography>
          </Box>
        )
      },
    },
    {
      field: 'agreementOption',
      headerName: 'Agreement Option',
      width: 150,
      headerAlign: 'center',
      valueGetter: params => {
        return params
      },
      renderCell: params => {
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'left',
              width: '100%',
              height: '100%',
              textAlign: 'left',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            <Typography sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 12 }}>
              {params?.value}
            </Typography>
          </Box>
        )
      },
    },

    {
      field: 'startDate',
      headerName: 'Start Date',
      width: 100,
      headerAlign: 'center',
      valueGetter: params => {
        return params ? formatDateString(params) : null
      },
      renderCell: params => {
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            {params && params.value ? params.value : ''}
          </Box>
        )
      },
    },
    {
      field: 'endDate',
      headerName: 'End Date',
      width: 100,
      headerAlign: 'center',
      valueGetter: params => {
        return params ? formatDateString(params) : null
      },
      renderCell: params => {
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            {params && params.value ? params.value : ''}
          </Box>
        )
      },
    },
    {
      field: 'organizations',
      headerName: 'Organization',
      width: 150, // or use flex: 1, minWidth: 160
      align: 'left',
      headerAlign: 'left',
      valueGetter: (_, row) => row?.organization?.name ?? null,
      renderCell: params => {
        const { PK } = params.row ?? {}
        const photoUrl = params.row?.organization?.photoUrl ?? null
        // const orgName = typeof params.value === 'string' ? params.value : 'Unknown Organization'
        const orgName = params?.row?.organization?.name ?? null

        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              width: '100%',
              height: '100%',
              minWidth: 0, // critical for ellipsis
              justifyContent: 'flex-start', // not "left"
            }}
          >
            <Avatar
              src={photoUrl || undefined}
              {...(!photoUrl && stringAvatar(orgName, { fontSize: densityStyles.fontSize }))}
              sx={{
                width: densityStyles.avatarSize,
                height: densityStyles.avatarSize,
                objectFit: 'cover',
                objectPosition: 'center',
                bgcolor: photoUrl ? undefined : 'rgb(114, 229, 218)',
              }}
            />

            <Link
              to={PK ? `/organizations/${PK}/details` : '#'}
              style={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                color: 'inherit',
                width: '100%',
                minWidth: 0, // critical
              }}
            >
              <Typography
                noWrap // adds whiteSpace + ellipsis
                sx={{
                  fontSize: densityStyles.fontSize,
                  fontWeight: 'bold',
                  flex: 1,
                  minWidth: 0, // critical
                  textAlign: 'left',
                }}
              >
                {orgName}
              </Typography>
            </Link>
          </Box>
        )
      },
    },
    {
      field: 'dealStatus',
      headerName: 'Deal Status',
      width: 100,
      headerAlign: 'center',
      valueGetter: params => {
        return params
      },
      renderCell: params => {
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <GridStatusChip size="small" type={params.value} label={params.value} />
          </Box>
        )
      },
    },
    {
      field: 'reasonChurn',
      headerName: 'Reason',
      width: 125,
      headerAlign: 'center',
      valueGetter: params => {
        return params
      },
      renderCell: params => {
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'left',
              width: '100%',
              height: '100%',
            }}
          >
            {params.value ? (
              <Typography sx={{ fontSize: 12 }}>{params.value}</Typography>
            ) : (
              <Typography sx={{ color: 'rgb(60, 57, 57)', fontSize: 14 }}>{'—'}</Typography>
            )}
          </Box>
        )
      },
    },
    {
      field: 'lastDateOfService',
      headerName: 'Date Ending',
      width: 100,
      headerAlign: 'center',
      valueGetter: params => {
        return params ? formatDateString(params) : null
      },
      renderCell: params => {
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            {params && params.value ? params.value : ''}
          </Box>
        )
      },
    },
    {
      field: 'churnNote',
      headerName: 'Notes',
      width: 125,
      headerAlign: 'center',
      valueGetter: params => {
        return params
      },
      renderCell: params => {
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'left',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              width: '100%',
              height: '100%',
            }}
          >
            {params.value ? (
              <Typography
                sx={{
                  fontSize: densityStyles.fontSize,
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                }}
              >
                {params.value}
              </Typography>
            ) : (
              <Typography
                sx={{
                  color: 'rgb(60, 57, 57)',
                  fontSize: densityStyles.fontSize,
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                }}
              >
                {'—'}
              </Typography>
            )}
          </Box>
        )
      },
    },
    {
      field: 'contractedBy',
      headerName: 'Contracted By',
      width: 100,
      headerAlign: 'left',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      valueGetter: (_, row) => {
        return row?.contractedBy?.name ?? null
      },
      renderCell: params => {
        const contractedById = params?.row?.contractedBy?.PK

        return (
          <>
            {params && params.value ? (
              <RenderContractedByColumn
                key={`${contractedById}`}
                orgName={params.value ?? ''}
                orgId={`ORG_${contractedById}`}
                densityStyles={densityStyles}
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'left',
                  alignItems: 'center',
                  height: '100%',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  padding: densityStyles.padding,
                  gap: densityStyles.gap,
                }}
              >
                <Typography
                  sx={{
                    color: 'rgb(60, 57, 57)',
                    fontSize: densityStyles.fontSize,
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                  }}
                >
                  {'—'}
                </Typography>
              </Box>
            )}
          </>
        )
      },
      sortComparator: (a, b) => {
        if (!a && !b) return 0 // Both are empty
        if (!a) return 1 // Treat empty as larger, so a (empty) goes after b (non-empty)
        if (!b) return -1 // Treat empty as larger, so b (empty) goes after a (non-empty)
        return a.localeCompare(b) // Standard string comparison for non-empty values
      },
    },

    {
      field: 'id',
      headerName: 'Vertical',
      headerAlign: 'center',
      sortable: false,
      valueGetter: (_, row) => {
        const deal = deals.find(deal => deal.deal_uuid === row?.id)
        const organization = row?.organization ?? null
        const verticals = organization && Array.isArray(organization.vertical) ? organization.vertical : []
        const verticalParams = verticals && Array.isArray(verticals) ? verticals[0] : []
        return verticalParams
      },
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      renderCell: params => {
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              width: '100%',
            }}
          >
            <RenderVertical
              vertical={params.value}
              width={densityStyles.verticalWidth}
              height={densityStyles.verticalHeight}
              size={densityStyles.verticalSize}
            />
          </Box>
        )
      },
    },
    // {
    //   field: 'creation_timestamp',
    //   headerName: 'Time Created',
    //   width: 100,
    //   headerAlign: 'left',
    //   renderCell: params => {
    //     return (
    //       <Box
    //         sx={{
    //           display: 'flex',
    //           flexDirection: 'row',
    //           justifyContent: 'center',
    //           alignItems: 'center',
    //           height: '100%',
    //           padding: densityStyles.padding,
    //           gap: densityStyles.gap,
    //         }}
    //       >
    //         {formatDayjsDate(params.value)}
    //       </Box>
    //     )
    //   },
    // },
    {
      field: 'stage',
      headerName: 'Deal Stage',
      width: 100,
      headerAlign: 'center',
      valueGetter: params => {
        return params
      },
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      renderCell: params => {
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              padding: densityStyles.padding,
              gap: densityStyles.gap,
            }}
          >
            <GridStageChip size="small" stage={params.value} label={params.value} />
          </Box>
        )
      },
    },
    {
      field: 'lastModified',
      headerName: 'Modified',
      width: 100,
      headerAlign: 'left',
      valueGetter: params => params,
      renderCell: params => {
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              padding: densityStyles.padding,
              gap: densityStyles.gap,
            }}
          >
            {formatDayjsDate(params && params.value ? params.value : 'No Modified Time')}
          </Box>
        )
      },
    },
    {
      field: 'timeCompleted',
      headerName: 'Date Won',
      width: 150,
      headerAlign: 'left',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customDateSortOperator,
      valueGetter: (_value, row) => {
        return row?.timeCompleted ? formatDateTime(row?.timeCompleted) : null
      },
      renderCell: params => <RenderDateCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'amId',
      headerName: 'Account Manager',
      width: 75,
      headerAlign: 'center',
      // sortable: false,
      valueGetter: (_, row) => {
        // return params ? { name: params?.name, image_512: params?.image_512 } : null
        const organization = row?.organization ?? null
        const team = organization?.team_details
        const teamAM = team?.find(u => u?.role === 'AM') ?? null
        const name = row?.amId ? row?.amId?.name : teamAM ? teamAM?.name : row?.owner ? row?.owner?.[0] : null
        return name ?? null
      },
      sortComparator: customSortOperator,
      renderCell: params => {
        const organization = params?.row?.organization ?? null
        const team = organization?.team_details
        const teamAM = team?.find(u => u?.role === 'AM' || u?.role === 'PM')
        const userId = teamAM
          ? teamAM?.user_uuid
          : params?.row?.amId
          ? params?.row?.amId?.user_uuid
          : params?.row?.owner
          ? params?.row?.owner?.[0]
          : null
        const user = users?.find(u => u?.user_uuid === userId)
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              padding: densityStyles.padding,
              gap: densityStyles.gap,
            }}
          >
            {user && user?.name && (user?.image_512 || user?.image) ? (
              <Tooltip title={<Typography>{user?.name}</Typography>}>
                <Avatar
                  src={user?.image_512}
                  sx={{ width: densityStyles.avatarSize, height: densityStyles.avatarSize }}
                  alt={user?.image_512}
                />
              </Tooltip>
            ) : (
              <Box
                sx={{
                  width: densityStyles.avatarSize,
                  height: densityStyles.avatarSize,
                  borderRadius: '50%',
                  backgroundColor: 'lightgray',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: 'rgba(0, 0, 0, 0.6)',
                  fontSize: densityStyles.fontSize,
                }}
              >
                <Avatar />
              </Box>
            )}
          </Box>
        )
      },
    },
    {
      field: 'billingContact',
      headerName: 'Billing Contact',
      width: 100,
      headerAlign: 'center',
      // sortable: false,
      valueGetter: (_, row) => {
        return row?.billingContact?.name ?? null
      },
      renderCell: params => {
        const billingContact = params?.row?.billingContact ?? null
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              padding: densityStyles.padding,
              gap: densityStyles.gap,
            }}
          >
            {billingContact && billingContact?.name ? (
              <Tooltip title={<Typography>{billingContact?.name}</Typography>}>
                {billingContact?.photoUrl ? (
                  <Avatar
                    src={billingContact?.photoUrl}
                    sx={{ width: densityStyles.avatarSize, height: densityStyles.avatarSize }}
                    alt={billingContact?.photoUrl}
                  />
                ) : (
                  <Typography>{billingContact?.name}</Typography>
                )}
              </Tooltip>
            ) : (
              <Box
                sx={{
                  width: densityStyles.avatarSize,
                  height: densityStyles.avatarSize,
                  borderRadius: '50%',
                  backgroundColor: 'lightgray',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: 'rgba(0, 0, 0, 0.6)',
                  fontSize: densityStyles.fontSize,
                }}
              >
                <Avatar />
              </Box>
            )}
          </Box>
        )
      },
    },
    {
      field: 'primaryContact',
      headerName: 'Primary Contact',
      width: 100,
      headerAlign: 'center',
      sortable: false,
      valueGetter: params => {
        return params ? params?.name : null
      },
      renderCell: params => {
        const user = params?.row?.primaryContact
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              padding: densityStyles.padding,
              gap: densityStyles.gap,
            }}
          >
            {user && user?.name && user?.photoUrl ? (
              <Tooltip title={<Typography>{user?.name}</Typography>}>
                <Avatar
                  src={user?.photoUrl}
                  sx={{ width: densityStyles.avatarSize, height: densityStyles.avatarSize }}
                  alt={user?.photoUrl}
                />
              </Tooltip>
            ) : (
              <Box
                sx={{
                  width: densityStyles.avatarSize,
                  height: densityStyles.avatarSize,
                  borderRadius: '50%',
                  backgroundColor: 'lightgray',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: 'rgba(0, 0, 0, 0.6)',
                  fontSize: densityStyles.fontSize,
                }}
              >
                <Avatar />
              </Box>
            )}
          </Box>
        )
      },
    },

    {
      field: 'implementationFee',
      headerName: 'Impl. Fee',
      width: 100,
      headerAlign: 'center',
      valueGetter: params => params,
      renderCell: params => {
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              padding: densityStyles.padding,
              gap: densityStyles.gap,
            }}
          >
            {params.value}
          </Box>
        )
      },
    },
    {
      field: 'total',
      headerName: 'Total',
      width: 100,
      headerAlign: 'center',
      valueGetter: params => params,
      renderCell: params => {
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              padding: densityStyles.padding,
              gap: densityStyles.gap,
            }}
          >
            {formatCurrency(params.value)}
          </Box>
        )
      },
    },
  ]
}
